//* Importing the necessary modules
import { useState, useCallback, useEffect } from "react";
//* Importing the useNavigate hook from react-router-dom
import { useNavigate } from "react-router-dom";
//! Importing the Common component
import { List } from "../../../component/list/list";
//* Importing the useRegistrations hook
import { useRegistrations } from "../../../hooks/registration/useRegistration";
//* Importing the useUnits hook
import { useUnits } from "../../../hooks/units/useUnits";
//* Importing the useCourses hook
import { useCourses } from "../../../hooks/courses/useCourses";
//? Importing the Registration type
import { Registration } from "../../../hooks/registration/types/types";
//* Importing the Dropdown, Button and Modal components from react-bootstrap
import { Dropdown, Button, Modal } from "react-bootstrap";
//? Importing the Unit type
import { Unit } from "../../../hooks/units/types/types";
//? Importing the Course type
import { Course } from "../../../hooks/courses/types/type";

var debounce = require("lodash.debounce");

export function RegistrationView(props: { title?: string }) {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [registrationToDelete, setRegistrationToDelete] = useState<
    string | null
  >(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [totalRegistrations, setTotalRegistrations] = useState<number>(0);
  // Estado para armazenar seleções de todas as páginas
  const [selectedRegistrations, setSelectedRegistrations] = useState<string[]>(
    []
  );
  const [clearSelectionTrigger, setClearSelectionTrigger] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const {
    registrations,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteRegistration,
    refetch,
    changePage,
    isDeleting,
    filters,
    updateFilters,
    confirmMultipleRegistrations,
    isConfirmingMultiple,
  } = useRegistrations(token);

  const { units, error: unitsError } = useUnits(token);
  const { courses, error: coursesError } = useCourses(token);

  useEffect(() => {
    setTotalRegistrations(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      if (trimmedValue.length >= 3) {
        setSearch(trimmedValue);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (registrationToDelete) {
      try {
        const currentPageItems = registrations.length;
        const currentPage = pagination.currentPage;
        await deleteRegistration(registrationToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setRegistrationToDelete(null);
      } catch (error) {
        console.error("Error deleting registration:", error);
      }
    }
  }, [
    registrationToDelete,
    deleteRegistration,
    registrations.length,
    pagination.currentPage,
    changePage,
  ]);

  // Ação de exclusão
  const handleDeleteActionClick = useCallback((id: string) => {
    setRegistrationToDelete(id);
    setShowWarning(true);
  }, []);

  // Muda a página
  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const handleUnitChange = useCallback(
    (newUnitId: string | undefined) => {
      updateFilters({ unitId: newUnitId, courseId: undefined });
    },
    [updateFilters]
  );

  const handleCourseChange = useCallback(
    (newCourseId: string | undefined) => {
      updateFilters({ courseId: newCourseId });
    },
    [updateFilters]
  );

  const handleIsActiveChange = useCallback(
    (isActive: boolean | undefined) => {
      updateFilters({ isActive });
    },
    [updateFilters]
  );

  // Função para manter a seleção acumulada entre páginas
  const handleSelectedItemsChange = useCallback(
    (currentPageSelectedIds: string[]) => {
      // IDs dos itens na página atual
      const currentPageIds = registrations.map((reg) => reg.id);
      setSelectedRegistrations((prevSelected) => {
        // Mantém os itens selecionados de outras páginas
        const selectionsOtherPages = prevSelected.filter(
          (id) => !currentPageIds.includes(id)
        );
        // Mescla com os itens selecionados da página atual
        return [...selectionsOtherPages, ...currentPageSelectedIds];
      });
    },
    [registrations]
  );

  // Componente de filtro extra
  const filterComponents = (
    <div className="d-flex align-items-center flex-wrap">
      {/* Unidade */}
      <Dropdown className="d-flex align-items-center ms-2">
        <Dropdown.Toggle variant="success" id="dropdown-unit">
          {filters.unitId
            ? units.find((unit: Unit) => unit.id === filters.unitId)?.name
            : "Todas as Unidades"}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
          <Dropdown.Item onClick={() => handleUnitChange(undefined)}>
            Todas as Unidades
          </Dropdown.Item>
          {units.map((unit: Unit) => (
            <Dropdown.Item
              key={unit.id}
              onClick={() => handleUnitChange(unit.id)}
            >
              {unit.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {/* Curso */}
      <Dropdown className="d-flex align-items-center ms-2">
        <Dropdown.Toggle variant="success" id="dropdown-course">
          {filters.courseId
            ? courses.find((course: Course) => course.id === filters.courseId)
                ?.name
            : "Todos os Cursos"}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
          <Dropdown.Item onClick={() => handleCourseChange(undefined)}>
            Todos os Cursos
          </Dropdown.Item>
          {courses.map((course: Course) => (
            <Dropdown.Item
              key={course.id}
              onClick={() => handleCourseChange(course.id)}
            >
              {course.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {/* Status Ativo/Inativo */}
      <Dropdown className="d-flex align-items-center ms-2">
        <Dropdown.Toggle variant="info" id="dropdown-active-status">
          {filters.isActive === true
            ? "Ativos"
            : filters.isActive === false
            ? "Inativos"
            : "Todos"}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => handleIsActiveChange(undefined)}>
            Todos
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleIsActiveChange(true)}>
            Ativos
          </Dropdown.Item>
          <Dropdown.Item onClick={() => handleIsActiveChange(false)}>
            Inativos
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  // Monta os itens para passar ao <List>
  const listItems = registrations.map((registration: Registration) => ({
    ...registration,
    key: registration.id,
    class: registration.team?.name || "",
    student: registration.student?.name || "",
    unit: registration.team?.unit || "",
    course: registration.team?.course || "",
    active:
      String(registration.isActive).toUpperCase() === "TRUE" ? "SIM" : "NÃO",
    canSelect: !registration.isActive,
  }));

  // Abre modal de confirmação das matrículas selecionadas
  const handleOpenConfirmModal = useCallback(() => {
    if (selectedRegistrations.length === 0) {
      alert("Nenhum registro selecionado.");
      return;
    }
    setShowConfirmModal(true);
  }, [selectedRegistrations]);

  // Fecha modal
  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmMultipleRegistrations = useCallback(async () => {
    try {
      await confirmMultipleRegistrations(selectedRegistrations);
      setSelectedRegistrations([]);
      setShowConfirmModal(false);
      setPagination((prev) => ({ ...prev }));
      setClearSelectionTrigger((prev) => !prev);
    } catch (error) {
      console.error("Error confirming registrations:", error);
      alert("Erro ao confirmar matrículas.");
    }
  }, [selectedRegistrations, confirmMultipleRegistrations, setPagination]);

  // Botão personalizado para passar ao componente <List>
  const customButton = (
    <Button
      variant="primary"
      onClick={handleOpenConfirmModal}
      className="d-flex align-items-center"
      disabled={selectedRegistrations.length === 0}
    >
      Confirmar Matrículas Selecionadas ({selectedRegistrations.length})
    </Button>
  );

  if (error || unitsError || coursesError) {
    return <div>Error: {error || unitsError || coursesError}</div>;
  }

  return (
    <div className="row">
      {/* Modal de confirmação */}
      <Modal show={showConfirmModal} onHide={handleCloseConfirmModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Matrículas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Você tem certeza que deseja confirmar as matrículas selecionadas?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseConfirmModal}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmMultipleRegistrations}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <List
        hideManager={true}
        hideDelete={true}
        search_input_value={searchInputValue}
        search_text="Pesquise pelo nome da matrícula, estudante ou turma"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={props.title ?? `Matrículas (Total: ${totalRegistrations})`}
        not_found_message="Nenhuma matrícula encontrada"
        button_link="/registrations/create"
        button_text="Adicionar matrícula"
        deleting_message="Ao deletar esta matrícula, ela não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/registrations/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={
          loading || isSearching || isConfirmingMultiple || isDeleting
        }
        items={listItems}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "class", type: "text", header: "Turma" },
          { name: "student", type: "text", header: "Estudante" },
          { name: "unit", type: "text", header: "Unidade" },
          { name: "course", type: "text", header: "Curso" },
          { name: "active", type: "text", header: "Ativo" },
        ]}
        loading_search={isSearching}
        additionalFilters={filterComponents}
        enableSelection
        // Usamos a função que acumula as seleções de todas as páginas
        onSelectedItemsChange={handleSelectedItemsChange}
        clearSelectionTrigger={clearSelectionTrigger}
        customButton={customButton}
        selectedItemsCount={selectedRegistrations.length}
      />
    </div>
  );
}
